import { DocumentTarget } from 'core/types'

// Mirrors BusinessLogic/Domain/DocumentDomain/DocumentRequirementModel.cs

export interface DocumentPerson {
  id: number
  name: string
  isChild: boolean
}

export interface DocumentSummary {
  documentID: number
  fileName: string
  whenUploaded: string
  verified: boolean
  incomeAmount: number | null
  incomeYear: number | null
  appliesTo: DocumentPerson[]
}

export interface DocumentRequirement {
  documentTypeID: number
  name: string
  description: string | null
  imageS3Key: string | null
  incomeRelated: boolean
  target: DocumentTarget
  caregiverOptions: DocumentPerson[]
  documents: DocumentSummary[]
}

export interface DocumentRequirements {
  child: DocumentPerson
  canAddDocuments: boolean
  requirements: DocumentRequirement[]
}

/** A document the user has picked a file for but not yet included. Nothing is sent to the server until it is included. */
export interface PendingDocument {
  documentTypeID: number
  file: File
  includeChild: boolean
  caregiverIDs: number[]
  incomeAmount: number | null
  incomeYear: number | null
}

/** Resolves to the error messages from the save, or an empty array on success. */
export type AddDocumentHandler = (pending: PendingDocument) => Promise<string[]>

export const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.heic'
export const INCOME_YEARS_BACK = 6 // matches DocumentRequirements.IncomeYearsBack on the server

export function isSatisfied (requirement: DocumentRequirement): boolean {
  return requirement.documents.length > 0
}

/** People a user must choose between for this document. Empty when the target is Child only — the child is assigned automatically. */
export function appliesToOptions (requirement: DocumentRequirement, child: DocumentPerson): DocumentPerson[] {
  switch (requirement.target) {
    case DocumentTarget.CHILD: return []
    case DocumentTarget.ADULT: return requirement.caregiverOptions
    default: return [child, ...requirement.caregiverOptions]
  }
}

export function incomeYearOptions (currentYear: number): number[] {
  return Array.from({ length: INCOME_YEARS_BACK + 1 }, (_, i) => currentYear - i)
}

export function newPendingDocument (requirement: DocumentRequirement, file: File): PendingDocument {
  const options = appliesToOptions(requirement, { id: 0, name: '', isChild: true })
  const onlyCaregiver = options.length === 1 && !options[0].isChild ? [options[0].id] : []
  return {
    documentTypeID: requirement.documentTypeID,
    file,
    includeChild: requirement.target === DocumentTarget.CHILD,
    caregiverIDs: onlyCaregiver, // a single choice is pre-selected (MIECC-2269: "they are the only choice offered")
    incomeAmount: null,
    incomeYear: null
  }
}

export function pendingDocumentErrors (requirement: DocumentRequirement, pending: PendingDocument): string[] {
  const errors: string[] = []
  if (requirement.target !== DocumentTarget.CHILD && !pending.includeChild && pending.caregiverIDs.length === 0) {
    errors.push('Select who this document applies to')
  }
  if (requirement.incomeRelated) {
    if (pending.incomeAmount === null || pending.incomeAmount < 0) errors.push('Enter the income amount')
    if (pending.incomeYear === null) errors.push('Select the income year')
  }
  return errors
}
